import React, { useState } from 'react';
import queryString from 'query-string';
import styled from 'styled-components/macro';
import { FormikHelpers } from 'formik';
import { RouteComponentProps } from 'react-router-dom';
import { Tables, Button } from '@deeptrue-frontend/ui';
import { AssetType } from 'store/modules/assets/assets.types';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import ROUTES from 'consts/routing';
import Asset from './Asset';
import Forms from 'components/Forms';
import { useFormatMessage } from 'components/IntlProvider';
import { FormValues as NewAssetProps } from 'components/Forms/AssetAddNew';
import api from 'api';
import { translateFieldErrors } from 'components/IntlProvider/translateFieldErrors';

const { withRouter } = RouterProvider;

const StyledButton = styled(Button)`
  margin: 0 0 2rem;
`;

const { Table, TableHeader, TableBody, TableFooter } = Tables;

interface Props extends RouteComponentProps {
  assetList: Array<AssetType>;
  count: number;
  currentPage: number;
  removeAsset: (uuid: string) => void;
}

const AssetsList = ({
  assetList,
  count,
  currentPage,
  history,
  removeAsset,
}: Props) => {
  const [isAddNewAssetOpen, setAddNewAssetOpen] = useState(false);
  const t = useFormatMessage();

  const onChangePage = (page: number) => {
    const url = ROUTES.ADMIN.ASSETS.LIST;
    const params = { limit: 20, offset: 20 * page - 20 };
    history.push(`${url}?${queryString.stringify(params)}`);
  };

  const uploadAsset = (
    values: NewAssetProps,
    { setErrors }: FormikHelpers<NewAssetProps>
  ) => {
    const { context_name, name, asset, language, is_global } = values;
    const formData = new FormData();
    formData.append('asset', asset);
    formData.append('name', name || '');
    formData.append('context_name', context_name || '');
    formData.append('language', language);
    formData.append('is_global', JSON.stringify(is_global));

    return api.resources
      .createAsset(formData)
      .then(() => {
        setAddNewAssetOpen(false);
        history.push(`${ROUTES.ADMIN.ASSETS.LIST}?action=asset_added`);
      })
      .catch((err) => {
        setErrors(
          translateFieldErrors({
            t,
            err: err.response.data,
          })
        );
      });
  };
  return (
    <>
      <Forms.AssetAddNew
        initialValues={{
          name: '',
          context_name: '',
          language: 'en',
          asset: '',
          is_global: false,
        }}
        isVisible={isAddNewAssetOpen}
        onClose={() => setAddNewAssetOpen(false)}
        uploadAsset={uploadAsset}
      />
      <StyledButton variant="primary" onClick={() => setAddNewAssetOpen(true)}>
        {t('assets.addNew', 'Add new Asset')}
      </StyledButton>
      <Table>
        <TableHeader
          cells={[
            t('table.asset.header.assetPreview', 'Asset Preview'),
            t('table.asset.header.assetName', 'Asset Name'),
            t('table.asset.header.language', 'Language'),
            t('table.asset.header.url', 'Url'),
            t('table.asset.header.actions', 'Actions'),
            '',
          ]}
        />
        <TableBody>
          {assetList.map((asset: AssetType) => (
            <Asset asset={asset} key={asset.uuid} removeAsset={removeAsset} />
          ))}
        </TableBody>
        <TableFooter>
          <Tables.TablePagination
            count={count}
            currentPage={currentPage}
            rowsPerPage={20}
            onChangePage={onChangePage}
          />
        </TableFooter>
      </Table>
    </>
  );
};

export default withRouter(AssetsList);
